import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useState, useMemo } from "react";

import {
	validationSchema,
	getSchemas,
	initialValues,
} from "./energy";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import type { ModuleFull } from "./energyTypes";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useContextAwareGetEnergy,
	useContextAwareGetEnergyEntries,
	useUpdateEnergiesMutation,
	useUpdateEnergyEntryMutation,
} from "@hooks/index";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import EnergyTierOne from "./EnergyTierOne";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import GenericTierTwo from "../GenericTierTwo";

const Energy = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const id = moduleList.find((module) => module.id === FEModules.Energy)?.uniqueId;

	const { data: energiesData, isLoading: isEnergiesLoading, isError: isEnergiesError, error: energiesError } = useContextAwareGetEnergy(id ?? 0, { refetchOnMountOrArgChange: true });
	const { data: energyEntriesData, isLoading: isEnergyEntriesLoading, isError: isEnergyEntriesError, error: energyEntriesError } = useContextAwareGetEnergyEntries({ activityId: activeActivityId ?? 0 }, { refetchOnMountOrArgChange: true });
	const [updateEnergies, { isError: isUpdateEnergiesError, error: updateEnergiesError }] = useUpdateEnergiesMutation();
	const [updateEnergyEntry, { isError: isUpdateEnergyEntryError, error: updateEnergyEntryError, isLoading: isUpdateEnergyEntryLoading }] = useUpdateEnergyEntryMutation();

	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>(initialValues);
	const { errorMsg: updateParentErrorMsg, tabsOpen } = useModuleHook({
		skeletonsLoadingDeps: [
			isEnergiesLoading,
			isEnergyEntriesLoading,
		],
		isMutateError: isUpdateEnergiesError,
		mutateError: updateEnergiesError,
	});
	const { errorMsg: fetchError } = useErrorMessage({
		isError: isEnergyEntriesError || isEnergiesError,
		error: energyEntriesError ?? energiesError,
	});
	const { errorMsg: updateEntryErrorMsg } = useErrorMessage({
		isError: isUpdateEnergyEntryError,
		error: updateEnergyEntryError,
	});
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	useEffect(() => {
		if (!energiesData) return;
		setInitValues((current) => ({ ...current, parent: energiesData }));
	}, [energiesData]);

	useEffect(() => {
		if (!energyEntriesData) return;
		processModuleThreads({ energyEntries: energyEntriesData });
	}, [energyEntriesData, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		//update notes
		const { parent, energyEntries } = values;
		try {
			const promises: Promise<any>[] = [];
			const parentPayload = { body: { ...parent }, activityId: activeActivityId ?? 0 }

			// Update energyEntries
			energyEntries.forEach((entry) => {
				const payload = { body: entry, activityId: activeActivityId ?? 0 }
				if (entry.id) promises.push(updateEnergyEntry(payload))
				return Promise.resolve()
			})

			await Promise.all(promises);
			await new Promise(resolve => setTimeout(resolve, 500));
			if (parent.id) updateEnergies(parentPayload)
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<EnergyTierOne
					title="project.energy"
					initValues={initValues}
					t2Schema={t2Schema}
					beModuleType={BEModulesClassNames.Energy}
					moduleSchema={moduleSchema}
					isLoading={isEnergiesLoading || isEnergyEntriesLoading || isUpdateEnergyEntryLoading}
					isEntriesLoading={isEnergyEntriesLoading}
					footerError={updateParentErrorMsg ?? updateEntryErrorMsg}
					blockingError={fetchError}
					setInitValues={setInitValues}
				/>
				<GenericTierTwo
					title="project.energy"
					t2Schema={t2Schema}
					tabsOpen={tabsOpen}
				/>
			</>
		</Formik>
	);
};

export default Energy;
